import React, { useState } from 'react';
import { 
    Box, 
    Button, 
    IconButton, 
    List, 
    ListItem, 
    ListItemSecondaryAction, 
    ListItemText, 
    Paper, 
    Typography 
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import { CustomField, FieldType } from '../../../entities/business/LeakCheckFormTemplate';
import { useLeakCheckForm } from './LeakCheckFormContext';
import { FieldEditorDialog } from './FieldEditorDialog';

interface CustomFieldsListProps {
    title: string;
    fields: CustomField[];
    sectionType: string;
    sectionIndex?: number;
}

export const CustomFieldsList: React.FC<CustomFieldsListProps> = ({ 
    title, 
    fields, 
    sectionType, 
    sectionIndex 
}) => {
    const { addCustomFieldToSection, removeCustomField, updateCustomField } = useLeakCheckForm();
    const [editingField, setEditingField] = useState<CustomField | null>(null);

    const handleAddField = () => {
        addCustomFieldToSection(sectionType, sectionIndex);
    };

    const handleEditField = (field: CustomField, index: number) => {
        setEditingField({ ...field });
    };

    const handleDeleteField = (index: number) => {
        removeCustomField(sectionType, index, sectionIndex);
    };

    const handleSaveField = (updatedField: CustomField) => {
        const fieldIndex = fields.findIndex(f => f.id === updatedField.id);
        if (fieldIndex !== -1) {
            updateCustomField(sectionType, fieldIndex, updatedField, sectionIndex);
        }
        setEditingField(null);
    };

    return (
        <>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1">
                    {title}
                </Typography>
                <Button 
                    startIcon={<AddIcon />}
                    color="primary"
                    onClick={handleAddField}
                >
                    Add Field
                </Button>
            </Box>
            <Paper variant="outlined">
                <List>
                    {fields.length === 0 ? (
                        <ListItem>
                            <Typography color="textSecondary">No custom fields added</Typography>
                        </ListItem>
                    ) : (
                        fields.map((field, idx) => (
                            <ListItem key={field.id} divider={idx < fields.length - 1}>
                                <ListItemText 
                                    primary={field.label}
                                    secondary={`${FieldType[field.type]}${field.isRequired ? ' • Required' : ''}`}
                                />
                                <ListItemSecondaryAction>
                                    <IconButton 
                                        edge="end" 
                                        onClick={() => handleEditField(field, idx)}
                                        size="small"
                                        style={{ marginRight: 8 }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton 
                                        edge="end" 
                                        onClick={() => handleDeleteField(idx)}
                                        size="small"
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))
                    )}
                </List>
            </Paper>
            
            {editingField && (
                <FieldEditorDialog
                    open={!!editingField}
                    onClose={() => setEditingField(null)}
                    field={editingField}
                    onSave={handleSaveField}
                />
            )}
        </>
    );
};